import Container, {
  getContainerNodeStylesObject,
  getContainerNodeStyles,
  type ContainerProps,
} from '@builder4/shared/components/Container';
import { Resizer } from '../Resizer';
import OptionPlaceholder from './OptionPlaceholder';
import BlankPlaceholder from './BlankPlaceholder';
import { InspectorContent } from '@/components/inspector/InspectorContent/InspectorContent';
import { useNode, useEditor, type Node, type NodeHelpersType, ROOT_NODE } from '@craftjs/core';
import clsx from 'clsx';
import { getVisibilityClasses } from '@builder4/shared/utils/styleHelpers';
import { hasFixedHeightV2, isSingleRootContainer, removeUndefined } from '@/utils/helperFunc';
import { CONTENT_BLOCK_NAME, FORM_NODE_NAME, POPUP_NODE_NAME } from '@/utils/constants';
import { isFormSubNode } from '@builder4/shared/components/Form/formRegistry';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getStaggerDataAttribute,
} from '@builder4/shared/utils/animationHelpers';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import { useAppSelector } from '@/store/hooks';
import { selectEffectiveBreakpoint, selectIsFullscreenPreview } from '@/store/system/selectors';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
// Wrapper component that provides the Resizer, placeholders, and style helpers to the shared Container
export const EditorContainer = (props: Partial<ContainerProps>) => {
  const {
    isEmpty,
    isActive,
    connectors: { connect, drag },
    id,
    parent,
    nodeProps,
    node,
  } = useNode((node) => ({
    isEmpty: node.data.nodes.length === 0,
    isActive: node.events.selected,
    id: node.id,
    parent: node.data.parent,
    node,
    nodeProps: node.data.props as ContainerProps,
  }));
  const { activeLayer, hoveredLayer, query } = useEditor((_, query) => ({
    activeLayer: query.getEvent('selected').all()[0],
    hoveredLayer: query.getEvent('hovered').all()[0],
  }));
  const breakpoint = useAppSelector(selectEffectiveBreakpoint);
  const isFullscreenPreview = useAppSelector(selectIsFullscreenPreview);
  const { environment } = useRenderContext();
  const { getBPProp } = useBreakpointAwareProp(CONTENT_BLOCK_NAME);

  // Extract props for styling - use breakpoint-aware reading for all props
  const fillSpace = getBPProp(nodeProps, 'fillSpace');
  const minWidth = getBPProp(nodeProps, 'minWidth');
  const visibility = getBPProp(nodeProps, 'visibility');
  const placeholderHeight = getBPProp(nodeProps, 'placeholderHeight');
  const padding = getBPProp(nodeProps, 'padding');
  const borderWidth = getBPProp(nodeProps, 'borderWidth');
  const disableMaxWidth = getBPProp(nodeProps, 'disableMaxWidth');
  const maxWidth = getBPProp(nodeProps, 'maxWidth');
  // Generate visibility classes instead of using inline display styles
  const visibilityClasses = getVisibilityClasses(visibility);

  // Check if this container should show active tree
  const isRootContainer = parent === 'ROOT';
  const descendants = isRootContainer ? query.node(id).descendants(true) : [];
  descendants.unshift(id);
  const activeTree = descendants.includes(activeLayer) || descendants.includes(hoveredLayer);
  const isFixedHeight = hasFixedHeightV2(getBPProp(nodeProps, 'height'));

  const placeholderHeightString = isFixedHeight
    ? `100%`
    : `${placeholderHeight - (padding[0] + padding[2] + borderWidth * 2)}px`;

  //for styles prop we must override inner calculation of resizer lib for fillSpace and flexBasis
  const { mobile, tablet, ...computedStyles } = getContainerNodeStylesObject(props);
  let currentBpFlexStyle: object = computedStyles;
  if (breakpoint === 'mobile' && mobile) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(mobile) };
  } else if (breakpoint === 'tablet' && tablet) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(tablet) };
  } else if (isFullscreenPreview) {
    // WHY: In fullscreen preview, the canvas renders at real viewport width. Applying
    // width/flex as inline styles would override CSS container queries, preventing
    // responsive layout from firing at small viewport sizes. Instead, these are moved
    // to a CSS class (nodeResponsiveClass) so @container rules can change them to
    // match published page behaviour.
    currentBpFlexStyle = Object.fromEntries(
      Object.entries(computedStyles as Record<string, unknown>).filter(
        ([key]) => key !== 'width' && key !== 'flex',
      ),
    );
  }

  // In fullscreen, apply node sizing (width/flex) via a CSS class with @container rules
  // instead of inline styles so responsive layout fires at small viewport widths.
  const nodeResponsiveClass = isFullscreenPreview
    ? getContainerNodeStyles(props, false)
    : undefined;

  const currentBgImage = getBPProp(nodeProps, 'bgImage');
  const currentBgColor = getBPProp(nodeProps, 'backgroundColor');
  const hasBackground = !!(currentBgImage || currentBgColor);
  const hideIllustration = !isRootContainer || hasBackground;
  const { animationType, animationDuration, animationDelay, animationEasing, staggerDelay } = props;
  // Animation/stagger on the whole node in builder so preview and selection frame match
  const animationAttrs = getAnimationDataAttributes({
    animationType,
    animationDuration,
    animationDelay,
    animationEasing,
  });
  const animationClass = getAnimationInitialClass(animationType, environment);
  const staggerAttrs = getStaggerDataAttribute(staggerDelay);

  const shouldShowOptionPlaceholder = isActive || isSingleRootContainer(node, query);
  return (
    <Resizer
      className={clsx(
        `container-node ${visibilityClasses} fit-content-height-imp`,
        {
          'flex-shrink-1-imp': fillSpace === 'yes',
          'active-tree': activeTree,
          animationClass,
        },
        nodeResponsiveClass,
      )}
      onRef={(ref) => {
        if (ref) {
          connect(drag(ref as HTMLElement));
        }
      }}
      maxWidth={disableMaxWidth ? undefined : maxWidth}
      minWidth={minWidth}
      propKey={{ width: 'width', height: 'height' }}
      style={currentBpFlexStyle}
      id={id}
      {...animationAttrs}
      {...staggerAttrs}
      data-animation-preview={id}
    >
      <Container {...props}>
        {!isEmpty ? (
          props.children
        ) : shouldShowOptionPlaceholder ? (
          <OptionPlaceholder placeholderHeight={placeholderHeightString} />
        ) : (
          <BlankPlaceholder
            placeholderHeight={placeholderHeightString}
            hideIllustration={hideIllustration}
          />
        )}
      </Container>
    </Resizer>
  );
};

type NodeQueryHelper = (id: string) => {
  get(): Node | null;
  childNodes(): string[];
};

EditorContainer.craft = {
  ...Container.craft,
  rules: {
    canDrag: (node: Node, helpers: NodeHelpersType) => {
      if (node.data.custom.isLocked) return false;
      const breakpoint = helpers(ROOT_NODE).get().data.props.breakpoint || 'desktop';
      const position = getBreakpointProp(
        node.data.props as Partial<ContainerProps>,
        'position',
        breakpoint,
      );

      return position === 'relative' || position === 'sticky';
    },
    canDrop: (node: Node) => !node.data.custom.isLocked,
    canMoveIn: (incomingNodes: Node[], currentNode: Node, helpers?: NodeQueryHelper) => {
      // Disallow placing form fields into generic containers
      const containsFormSubNode = incomingNodes.some((n) => isFormSubNode(n.data.displayName));
      if (containsFormSubNode) return false;

      // Disallow placing Popup into containers — Popups must be root-level children
      const containsPopup = incomingNodes.some((n) => n.data.displayName === POPUP_NODE_NAME);
      if (containsPopup) return false;

      // Disallow placing a Form inside any container that lives under a Form (no nested forms)
      const hasForm = incomingNodes.some((n) => n.data.displayName === FORM_NODE_NAME);
      if (hasForm && helpers) {
        let cursorId: string | null = currentNode.id as string;
        while (cursorId) {
          const cursor = helpers(cursorId).get();
          if (!cursor) break;
          const parentId = cursor.data.parent as string | undefined;
          if (!parentId) break;
          const parent = helpers(parentId).get();
          if (!parent) break;
          if (parent.data.displayName === FORM_NODE_NAME) {
            return false;
          }
          if (parentId === cursorId) break; // safety
          cursorId = parentId;
        }
      }

      return !currentNode.data.custom.isLocked;
    },
    canMoveOut: (outgoingNodes: Node[], currentNode: Node) => {
      console.log(outgoingNodes);
      return !currentNode.data.custom.isLocked;
    },
  },
  related: {
    toolbar: InspectorContent,
  },
};

export default EditorContainer;
